/**
 * Modular API Client
 *
 * Domain-driven API client with type-safe methods for each endpoint.
 *
 * Usage:
 * ```typescript
 * const { apiClient } = useApiClient();
 *
 * const projects = await apiClient.projects.getProjects();
 * const records = await apiClient.records.getRecords();
 * const user = await apiClient.users.getMe();
 * ```
 */

import type { AuthenticatedFetch } from './base';
import {
  RecordsApi,
  AnalysesApi,
  ScriptsApi,
  UsersApi,
  ProjectsApi,
  BillingApi,
  AlertsApi,
  AgentsApi,
  SettingsApi,
  NotificationsApi,
  AdminApi,
  InvitationsApi,
  FeatureFlagsApi,
  AccessApi,
  CrmApi,
  CrmFieldsApi,
  OrganizationsApi,
  OrgStructureApi,
  DeliveryRulesApi,
  InboundRoutingApi,
  CallStatsApi,
  ReportsApi,
  ConsentApi,
  DSRApi,
  HumanReviewApi,
  DealsApi,
  IncidentsApi,
  DemoApi,
  StageBindingsApi,
  SupportApi,
  AssistantApi,
  CallCriteriaApi,
  VoiceThemesApi,
} from './domains';

// Re-export everything for external use
export * from './base';
export * from './config';
export * from './endpoints';
export * from './types';
export * from './domains';

/**
 * API Client class with modular domain APIs
 *
 * Each domain is accessible as a property (e.g., apiClient.records)
 */
export class ApiClient {
  // Domain API instances
  public readonly records: RecordsApi;
  public readonly analyses: AnalysesApi;
  public readonly scripts: ScriptsApi;
  public readonly users: UsersApi;
  public readonly projects: ProjectsApi;
  public readonly billing: BillingApi;
  public readonly alerts: AlertsApi;
  public readonly agents: AgentsApi;
  public readonly settings: SettingsApi;
  public readonly notifications: NotificationsApi;
  public readonly admin: AdminApi;
  public readonly invitations: InvitationsApi;
  public readonly featureFlags: FeatureFlagsApi;
  public readonly access: AccessApi;
  public readonly crm: CrmApi;
  public readonly crmFields: CrmFieldsApi;
  public readonly organizations: OrganizationsApi;
  public readonly orgStructure: OrgStructureApi;
  public readonly deliveryRules: DeliveryRulesApi;
  public readonly inboundRouting: InboundRoutingApi;
  public readonly consent: ConsentApi;
  public readonly support: SupportApi;
  public readonly dsr: DSRApi;
  public readonly humanReview: HumanReviewApi;
  public readonly deals: DealsApi;
  public readonly callStats: CallStatsApi;
  public readonly reports: ReportsApi;
  public readonly incidents: IncidentsApi;
  public readonly demo: DemoApi;
  public readonly stageBindings: StageBindingsApi;
  public readonly assistant: AssistantApi;
  public readonly callCriteria: CallCriteriaApi;
  public readonly voiceThemes: VoiceThemesApi;

  constructor(
    authenticatedFetch: AuthenticatedFetch,
    baseUrl?: string,
    getAuthData?: () => Promise<{ token: string; selectedOrgId: string }>,
    blobFetch?: (path: string) => Promise<Response>
  ) {
    // Initialize all domain APIs
    this.records = new RecordsApi(authenticatedFetch, baseUrl, getAuthData, blobFetch);
    this.analyses = new AnalysesApi(authenticatedFetch, baseUrl);
    this.scripts = new ScriptsApi(authenticatedFetch, baseUrl);
    this.users = new UsersApi(authenticatedFetch, baseUrl);
    this.projects = new ProjectsApi(authenticatedFetch, baseUrl);
    this.billing = new BillingApi(authenticatedFetch, baseUrl);
    this.alerts = new AlertsApi(authenticatedFetch, baseUrl);
    this.agents = new AgentsApi(authenticatedFetch, baseUrl);
    this.settings = new SettingsApi(authenticatedFetch, baseUrl);
    this.notifications = new NotificationsApi(authenticatedFetch, baseUrl);
    this.admin = new AdminApi(authenticatedFetch, baseUrl);
    this.invitations = new InvitationsApi(authenticatedFetch, baseUrl);
    this.featureFlags = new FeatureFlagsApi(authenticatedFetch, baseUrl);
    this.access = new AccessApi(authenticatedFetch, baseUrl);
    this.crm = new CrmApi(authenticatedFetch, baseUrl);
    this.crmFields = new CrmFieldsApi(authenticatedFetch, baseUrl);
    this.organizations = new OrganizationsApi(authenticatedFetch, baseUrl);
    this.orgStructure = new OrgStructureApi(authenticatedFetch, baseUrl);
    this.deliveryRules = new DeliveryRulesApi(authenticatedFetch, baseUrl);
    this.inboundRouting = new InboundRoutingApi(authenticatedFetch, baseUrl);
    this.consent = new ConsentApi(authenticatedFetch, baseUrl);
    this.support = new SupportApi(authenticatedFetch, baseUrl);
    this.dsr = new DSRApi(authenticatedFetch, baseUrl);
    this.humanReview = new HumanReviewApi(authenticatedFetch, baseUrl);
    this.deals = new DealsApi(authenticatedFetch, baseUrl);
    this.callStats = new CallStatsApi(authenticatedFetch, baseUrl);
    this.reports = new ReportsApi(authenticatedFetch, baseUrl);
    this.incidents = new IncidentsApi(authenticatedFetch, baseUrl);
    this.demo = new DemoApi(authenticatedFetch, baseUrl);
    this.stageBindings = new StageBindingsApi(authenticatedFetch, baseUrl);
    this.assistant = new AssistantApi(authenticatedFetch, baseUrl);
    this.callCriteria = new CallCriteriaApi(authenticatedFetch, baseUrl);
    this.voiceThemes = new VoiceThemesApi(authenticatedFetch, baseUrl);
  }
}

// ============================================================================
// CENTRALIZED ERROR HANDLING
// ============================================================================

/**
 * API Error class with additional context
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Create ApiError from response data
   */
  static fromResponse(status: number, data: unknown): ApiError {
    const errorData = data as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    return new ApiError(
      status,
      errorData.error?.code || 'UNKNOWN_ERROR',
      errorData.error?.message || 'An unknown error occurred',
      errorData.error?.details
    );
  }
}

/**
 * Handle unknown errors and convert to ApiError
 */
export function handleApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof Error) {
    return new ApiError(500, 'INTERNAL_ERROR', error.message);
  }

  return new ApiError(500, 'UNKNOWN_ERROR', 'An unknown error occurred');
}
